/* Modulo: Benefit Tabs (bft)
 *
 * Emitted only when two or more tabs render. Owns four things:
 *   - the thumb: placed from the selected tab's offsetLeft/offsetWidth, which
 *     are physical in both directions, so RTL needs no mirroring;
 *   - tab switching with roving tabindex and Arrow / Home / End keys (arrows
 *     follow the reading direction);
 *   - the theme editor: selecting an item block opens the tab that holds it;
 *   - re-placing the thumb when the switch changes size (fonts, resize).
 *
 * Until it runs, the selected tab paints its own fill and the thumb stays
 * invisible, so first paint and no-JS both render correctly.
 */
(function () {
  'use strict';
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  var ROOT = '[data-modulo-bft]';

  function init(root) {
    if (!root || root.moduloBft) return;
    var sw = root.querySelector('[data-bft-switch]');
    if (!sw) return;
    var tabs = Array.prototype.slice.call(sw.querySelectorAll('[role="tab"]'));
    if (tabs.length < 2) return;

    function panelOf(tab) {
      return document.getElementById(tab.getAttribute('aria-controls'));
    }
    function current() {
      for (var i = 0; i < tabs.length; i++) {
        if (tabs[i].getAttribute('aria-selected') === 'true') return i;
      }
      return 0;
    }
    function place(tab) {
      sw.style.setProperty('--modulo-bft-thumb-x', tab.offsetLeft + 'px');
      sw.style.setProperty('--modulo-bft-thumb-w', tab.offsetWidth + 'px');
    }
    function select(index, focus) {
      var tab = tabs[index];
      if (!tab) return;
      for (var i = 0; i < tabs.length; i++) {
        var on = i === index;
        var panel = panelOf(tabs[i]);
        tabs[i].setAttribute('aria-selected', on ? 'true' : 'false');
        tabs[i].tabIndex = on ? 0 : -1;
        if (!panel) continue;
        if (on && panel.hidden) {
          panel.hidden = false;
          panel.classList.remove('is-entering');
          void panel.offsetWidth;
          panel.classList.add('is-entering');
        } else if (!on) {
          panel.hidden = true;
          panel.classList.remove('is-entering');
        }
      }
      place(tab);
      if (focus) tab.focus();
    }

    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () { select(i, false); });
      tab.addEventListener('keydown', function (e) {
        var rtl = getComputedStyle(sw).direction === 'rtl';
        var next = null;
        var fwd = rtl ? 'ArrowLeft' : 'ArrowRight';
        var back = rtl ? 'ArrowRight' : 'ArrowLeft';
        if (e.key === fwd) next = (i + 1) % tabs.length;
        else if (e.key === back) next = (i - 1 + tabs.length) % tabs.length;
        else if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = tabs.length - 1;
        if (next === null) return;
        e.preventDefault();
        select(next, true);
      });
    });

    // First placement happens with the thumb's transition off (the CSS keeps
    // it off until `is-ready`), and the forced layout commits it before the
    // class lands, so the thumb never slides in from the left edge.
    place(tabs[current()]);
    void sw.offsetWidth;
    root.classList.add('is-ready');

    var replace = function () { place(tabs[current()]); };
    if (typeof ResizeObserver === 'function') {
      new ResizeObserver(replace).observe(sw);
    } else {
      window.addEventListener('resize', replace);
    }
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(replace);

    root.moduloBft = {
      select: select,
      selectPanel: function (panel) {
        for (var i = 0; i < tabs.length; i++) {
          if (tabs[i].getAttribute('aria-controls') === panel.id) { select(i, false); return; }
        }
      },
      tabs: tabs
    };
  }

  function boot(scope) {
    var roots = (scope || document).querySelectorAll(ROOT);
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  // Document listeners are wired once: an identical <script src> still
  // executes once per tag, and the theme editor re-inserts sections.
  if (!window.moduloBftWired) {
    window.moduloBftWired = true;

    document.addEventListener('shopify:section:load', function (e) { boot(e.target); });

    // Selecting a block in the editor: a tab block opens itself, an item block
    // opens the tab that holds it, so the merchant always sees what they picked.
    document.addEventListener('shopify:block:select', function (e) {
      var el = e.target;
      if (!el || !el.closest) return;
      var root = el.closest(ROOT);
      if (!root || !root.moduloBft) return;
      if (el.getAttribute('role') === 'tab') {
        var idx = root.moduloBft.tabs.indexOf(el);
        if (idx > -1) root.moduloBft.select(idx, false);
        return;
      }
      var panel = el.closest('[data-bft-panel]');
      if (panel && panel.id) root.moduloBft.selectPanel(panel);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }
})();
